import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instagram: '',
    current_stage: '',
    biggest_challenge: '',
    life_change: '',
    readiness: '',
    investment_ready: '',
    expectations: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setCurrentStep(1);
        setFormData({ 
          name: '', 
          email: '', 
          instagram: '', 
          current_stage: '',
          biggest_challenge: '',
          life_change: '',
          readiness: '',
          investment_ready: '',
          expectations: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    // Validate step 1 fields
    if (formData.name && formData.email && formData.instagram && formData.life_change) {
      setCurrentStep(2);
      // Scroll to top of form
      document.getElementById('apply-form').scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBackStep = () => {
    setCurrentStep(1);
    document.getElementById('apply-form').scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToForm = () => {
    document.getElementById('apply-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content-text">
          <h1 className="hero-title">
            THE CONSCIOUS CREATOR MENTORSHIP
          </h1>
          <p className="hero-subtitle">
            Turn your message into content that grows your visibility, builds your brand, and creates real impact.
          </p>
          <div className="hero-stats">
            <p className="hero-proof">
              I went from <span className="highlight">0 to 150K+ followers</span> in 7 months — consistently reaching millions of views, without posting constantly.
            </p>
            <p className="hero-cta-text">
              This is the exact process I'll teach you.
            </p>
          </div>
          <Button 
            onClick={scrollToForm} 
            size="lg" 
            className="cta-button"
          >
            Apply for the Mentorship <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* The Truth */}
      <section className="truth-section">
        <div className="container">
          <h2 className="section-title">The Truth Most Creators Never Learn</h2>
          
          <div className="truth-content">
            <p>You've been told to post more. Test more. Go viral eventually.</p>
            <p className="emphasis">That's not how real growth works.</p>
            <p>
              Content that spreads isn't random. It's built on <span className="highlight-text">emotional structure</span> — 
              the way your message, hook, pacing, and visuals connect in the first seconds.
            </p>
            <p>When content feels complete, it performs differently.</p>
            <p className="emphasis-large">This mentorship teaches you exactly that.</p>
          </div>
        </div>
      </section>

      {/* What You'll Build */}
      <section className="build-section">
        <div className="container">
          <h2 className="section-title">What You'll Build in 3 Months</h2>
          
          <div className="phases">
            <div className="phase-card">
              <h3>Phase 1 — Identity & Clarity</h3>
              <p>
                Define who you are as a creator. Sharpen your niche, your voice, your story. 
                Without this, content stays random.
              </p>
            </div>

            <div className="phase-card">
              <h3>Phase 2 — Content & Viral Mechanics</h3>
              <p>
                Learn what makes people stop, watch, and engage. Hooks, pacing, storytelling, 
                emotional delivery — this is where you learn to consistently reach hundreds of 
                thousands to millions of views.
              </p>
            </div>

            <div className="phase-card">
              <h3>Phase 3 — Growth, Systems & Monetization</h3>
              <p>
                Build workflows that keep you consistent without burnout. Turn your audience into 
                income — through digital products, coaching, or aligned offers — without forcing sales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="included-section">
        <div className="container">
          <h2 className="section-title">What's Included</h2>
          
          <div className="included-list">
            <div className="included-item-simple">
              <CheckCircle2 className="check-icon" />
              <span>Weekly Live Group Calls (90–120 Min)</span>
            </div>
            <div className="included-item-simple">
              <CheckCircle2 className="check-icon" />
              <span>All Replays — Lifetime Access</span>
            </div>
            <div className="included-item-simple">
              <CheckCircle2 className="check-icon" />
              <span>Frameworks, Templates & Execution Tools</span>
            </div>
            <div className="included-item-simple">
              <CheckCircle2 className="check-icon" />
              <span>Private Community (3 Months)</span>
            </div>
            <div className="included-item-simple">
              <CheckCircle2 className="check-icon" />
              <span>Live Q&A + Ongoing Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="proof-section">
        <div className="container">
          <h2 className="section-title">Proof</h2>
          
          {/* Screenshots Grid */}
          <div className="proof-screenshots">
            <div className="screenshot-hero">
              <img 
                src="https://customer-assets.emergentagent.com/job_conscious-creator-1/artifacts/4902pjh4_Untitled_design.png" 
                alt="Content Performance - Millions of Views"
                className="proof-image-large"
              />
            </div>
            
            <div className="screenshot-stats-grid">
              <div className="stat-screenshot">
                <img 
                  src="https://customer-assets.emergentagent.com/job_conscious-creator-1/artifacts/hulu57k8_IMG_0251.jpeg" 
                  alt="323K Likes, 17K Comments"
                  className="proof-image-stat"
                />
              </div>
              <div className="stat-screenshot">
                <img 
                  src="https://customer-assets.emergentagent.com/job_conscious-creator-1/artifacts/sxuwmyiv_IMG_0247.jpeg" 
                  alt="464K Likes, 7.9K Comments"
                  className="proof-image-stat"
                />
              </div>
              <div className="stat-screenshot">
                <img 
                  src="https://customer-assets.emergentagent.com/job_conscious-creator-1/artifacts/wswz7e8m_IMG_0248.jpeg" 
                  alt="211K Likes, High Engagement"
                  className="proof-image-stat"
                />
              </div>
            </div>

            <div className="screenshot-growth-section">
              <h3 className="growth-section-title">Viral Growth in Real-Time</h3>
              <div className="growth-charts-grid">
                <div className="growth-chart-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conscious-creator-1/artifacts/yae0omg6_IMG_0268.jpeg" 
                    alt="0 to 3M views in 3 weeks"
                    className="proof-image-growth"
                  />
                  <p className="growth-caption">0 to 3M views in 3 weeks</p>
                </div>
                <div className="growth-chart-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conscious-creator-1/artifacts/0vtho255_IMG_0267.jpeg" 
                    alt="0 to 1M views in 7 days"
                    className="proof-image-growth"
                  />
                  <p className="growth-caption">0 to 1M views in 7 days</p>
                </div>
                <div className="growth-chart-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conscious-creator-1/artifacts/6tpiftkv_IMG_0244.jpeg" 
                    alt="Consistent exponential reach"
                    className="proof-image-growth"
                  />
                  <p className="growth-caption">Consistent exponential reach growth</p>
                </div>
              </div>
            </div>
          </div>

          <div className="proof-grid">
            <div className="proof-item">~28M total views per month</div>
            <div className="proof-item">Between 1.5M and 2.2M interactions monthly with minimal effort</div>
            <div className="proof-item">0 to 150K+ followers in 7 months</div>
            <div className="proof-item">Thousands of likes, comments, and shares on every post</div>
            <div className="proof-item">Thousands of pre-registrations for a digital experience</div>
          </div>
        </div>
      </section>

      {/* This Is For You If */}
      <section className="for-you-section">
        <div className="container">
          <h2 className="section-title">This Is For You If…</h2>
          
          <div className="for-you-content">
            <p className="lead-text">
              You have a real message — but you're tired of guessing why it's not reaching people.
            </p>
            <p className="emphasis-large">
              You're done with random posting. You want clarity, structure, and content that actually works.
            </p>
          </div>
        </div>
      </section>

      {/* Your Mentor */}
      <section className="mentor-section-short">
        <div className="container">
          <h2 className="section-title">Your Mentor</h2>
          
          <div className="mentor-story-short">
            <p className="mentor-intro">Hi, I'm Robin.</p>
            <p>At 33, I lost everything. Bitcoin gone. Business gone. Identity gone.</p>
            <p>What followed was years of rebuilding — from zero, in every sense.</p>
            <p className="emphasis">
              That experience became my greatest asset. Combined with the systems I built to grow 
              consistently to millions of views, I now help conscious creators find their voice and 
              make it visible.
            </p>
            <p className="final-mission">
              Because when the right voices are seen — the world becomes a better place.
            </p>
          </div>
        </div>
      </section>

      {/* Ready CTA */}
      <section className="ready-section">
        <div className="container">
          <div className="ready-cta">
            <h2>Ready?</h2>
            <p className="ready-text">Apply for the Mentorship</p>
            <p className="ready-subtext">Limited spots. Apply to see if it's the right fit.</p>
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Button 
                onClick={scrollToForm} 
                size="lg" 
                className="cta-button"
              >
                Apply for the Mentorship <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Apply Form */}
      <section className="apply-section" id="apply-form">
        <div className="container">
          <div className="apply-content">
            <div className="apply-header">
              <h2 className="section-title">Apply for The Conscious Creator Mentorship</h2>
              <p className="apply-subtitle">
                Spots are limited. We only work with people we know we can help.
              </p>
            </div>

            <Card className="apply-card">
              <CardContent className="apply-card-content">
                {submitStatus === 'success' ? (
                  <div className="success-message">
                    <CheckCircle2 className="success-icon" />
                    <h3>Application Submitted!</h3>
                    <p>Thank you for your interest. We'll review your application and get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={currentStep === 1 ? handleNextStep : handleSubmit} className="apply-form">
                    {currentStep === 1 ? (
                      <>
                        {/* Step 1: Basic Information */}
                        <div className="form-step">
                          <div className="form-group">
                            <label htmlFor="name">Full Name *</label>
                            <Input
                              id="name"
                              name="name"
                              type="text"
                              placeholder="Your name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              disabled={isSubmitting}
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="email">Email Address *</label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              placeholder="your@email.com"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              disabled={isSubmitting}
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="instagram">Instagram Handle *</label>
                            <Input
                              id="instagram"
                              name="instagram"
                              type="text"
                              placeholder="@yourhandle"
                              value={formData.instagram}
                              onChange={handleChange}
                              required
                              disabled={isSubmitting}
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label-question" htmlFor="life_change">Why do you want to join? *</label>
                            <Textarea
                              id="life_change"
                              name="life_change"
                              placeholder="Tell us about your current situation and what you hope to achieve..."
                              value={formData.life_change}
                              onChange={handleChange}
                              rows={5}
                              required
                              disabled={isSubmitting}
                            />
                          </div>

                          <Button type="submit" size="lg" className="submit-button">
                            Next <ArrowRight className="ml-2" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Step 2: Qualification Questions */}
                        <div className="form-step">
                          <div className="step-header">
                            <button type="button" onClick={handleBackStep} className="back-button">
                              ← Back
                            </button>
                            <p className="step-indicator">Step 2 of 2</p>
                          </div>

                          <div className="form-group">
                            <label className="form-label-question">Where are you right now?</label>
                            <div className="radio-group">
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="current_stage"
                                  value="starting_from_zero"
                                  checked={formData.current_stage === 'starting_from_zero'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm starting from zero and ready to build something real</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="current_stage"
                                  value="posting_but_stuck"
                                  checked={formData.current_stage === 'posting_but_stuck'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm already posting but stuck — nothing is growing</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="current_stage"
                                  value="coach_or_expert"
                                  checked={formData.current_stage === 'coach_or_expert'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm a coach or expert and want to attract clients through content</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="current_stage"
                                  value="audience_not_monetizing"
                                  checked={formData.current_stage === 'audience_not_monetizing'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I have an audience but I'm not monetizing it yet</span>
                              </label>
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label-question">What's your biggest challenge right now?</label>
                            <div className="radio-group">
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="biggest_challenge"
                                  value="content_not_spreading"
                                  checked={formData.biggest_challenge === 'content_not_spreading'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I don't know how to create content that actually spreads</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="biggest_challenge"
                                  value="inconsistent_no_system"
                                  checked={formData.biggest_challenge === 'inconsistent_no_system'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm inconsistent — I have no system behind my content</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="biggest_challenge"
                                  value="no_reach_engagement"
                                  checked={formData.biggest_challenge === 'no_reach_engagement'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I post regularly but get no reach or engagement</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="biggest_challenge"
                                  value="no_monetization"
                                  checked={formData.biggest_challenge === 'no_monetization'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I don't know how to turn my content into income</span>
                              </label>
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label-question">How ready are you to start?</label>
                            <div className="radio-group">
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="readiness"
                                  value="ready_immediately"
                                  checked={formData.readiness === 'ready_immediately'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm ready to start immediately if it's the right fit</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="readiness"
                                  value="gathering_information"
                                  checked={formData.readiness === 'gathering_information'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm still gathering information</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="readiness"
                                  value="few_months"
                                  checked={formData.readiness === 'few_months'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm planning to start in a few months</span>
                              </label>
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label-question">Are you ready to invest in your growth?</label>
                            <div className="radio-group">
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="investment_ready"
                                  value="serious_investment"
                                  checked={formData.investment_ready === 'serious_investment'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>Yes — I'm ready to make a serious financial investment if it's the right fit</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="investment_ready"
                                  value="limited_budget"
                                  checked={formData.investment_ready === 'limited_budget'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I have a limited budget of under €500</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="investment_ready"
                                  value="not_sure"
                                  checked={formData.investment_ready === 'not_sure'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>I'm not sure yet</span>
                              </label>
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label-question">What do you expect from a mentorship like this?</label>
                            <div className="radio-group">
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="expectations"
                                  value="ready_to_work"
                                  checked={formData.expectations === 'ready_to_work'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>A clear system and real guidance — I'm ready to do the work</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="expectations"
                                  value="do_it_for_me"
                                  checked={formData.expectations === 'do_it_for_me'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>Someone to do it for me</span>
                              </label>
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  name="expectations"
                                  value="quick_results"
                                  checked={formData.expectations === 'quick_results'}
                                  onChange={handleChange}
                                  required
                                  disabled={isSubmitting}
                                />
                                <span>Quick results without much effort</span>
                              </label>
                            </div>
                          </div>

                          {submitStatus === 'error' && (
                            <div className="error-message">
                              There was an error submitting your application. Please try again.
                            </div>
                          )}

                          <Button type="submit" size="lg" className="submit-button" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Apply Now'} <ArrowRight className="ml-2" />
                          </Button>
                        </div>
                      </>
                    )}
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="footer-text">
            © 2024 The Conscious Creator Mentorship. All rights reserved.
          </p>
          <p className="footer-tagline">
            Turning voices into visibility, content into impact.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
